import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {useTranslation} from 'react-i18next';

import desktopSkeletonImage from "../../../../resource/image/truckDesktop.jpg";
import hydrogenSkeletonImage from "../../../../resource/image/e-truck.jpg";


import "./SwiperSlider.css";
import i18n from "../../../../globals/i18n";

import {ProgressBar} from "./ProgressBar";
import {HotspotLink} from "./HotspotLink";

const autoplaySpeed = 7000; // 7 seconds

const CustomArrow = (props: any) => {
    const {className, style, onClick, direction} = props;
    const arrowPath =
        direction === "prev"
            ? "M15 17l-5-5 5-5"
            : "M9 17l5-5-5-5"; // SVG Path for previous or next arrow
    return (
        <div
            className={`${className} custom-${direction}-arrow`}
            style={{
                ...style,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff"
            }}
            onClick={onClick}
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                stroke="black"
                fill="none"
            >
                <path d={arrowPath} strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
        </div>
    );
};

const HomeCarousel = () => {
    const [progress, setProgress] = useState(0);
    const {t} = useTranslation();
    const ReactSlickSlider = Slider as unknown as React.FC<any>;

    const slides = useMemo(() => [
        {
            id: 1,
            title: "Mercedes-Benz Trucks",
            subtitle: "Operating Fluids",
            image: desktopSkeletonImage,
            dotText: t('Diesel'),
            hotspots: [
                {
                    id: 1,
                    label: "EngineOil",
                    classes: "slide1 sengineNavigatorDesktop",
                    link: `/category/engine-oil/${i18n.language}`
                },
                {
                    id: 2,
                    label: "GearOil",
                    classes: "slide1 sgearNavigatorDesktop",
                    link: `/category/gear-oil/${i18n.language}`
                },
                {
                    id: 3,
                    label: "AdBlue",
                    classes: "slide1 sadBlueNavigatorDesktop",
                    link: `/category/NOx-reducing-agent/${i18n.language}`
                },
                {
                    id: 4,
                    label: "Axle-Oil",
                    classes: "slide1 saxleOilNavigatorDesktop",
                    link: `/category/Axle-Oil/${i18n.language}`
                },
                {
                    id: 5,
                    label: "Coolant",
                    classes: "slide1 scoolantNavigatorDesktop",
                    link: `/category/coolant/${i18n.language}`
                },
            ],
        },
        {
            id: 2,
            title: "Mercedes-Benz Trucks",
            subtitle: "Operating Fluids",
            dotText: t('electricTruck'),
            image: hydrogenSkeletonImage,
            hotspots: [
                {
                    id: 7,
                    label: "edAC-Oil",
                    classes: "slide2 sedacNavigatorDesktop",
                    link: `/category/edAC-Oil/${i18n.language}`
                },
                {
                    id: 8,
                    label: "E-Drive",
                    classes: "slide2 sedriveNavigatorDesktop",
                    link: `/category/E-Drive/${i18n.language}`
                },
            ],
        },
    ], [t]);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const startProgress = () => {
            setProgress(0);
            intervalRef.current = setInterval(() => {
                setProgress((prev) => (prev < 100 ? prev + 1 : 100));
            }, autoplaySpeed / 100);
        };

        startProgress();

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoplaySpeed]);


    const handleBeforeChange = useCallback(() => {
        setProgress(0); // Reset progress when slide changes
    }, []);

    const settings = useMemo(() => ({
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed,
        cssEase: "linear",
        swipeToSlide: true,
        arrows: true,
        prevArrow: <CustomArrow direction="prev"/>,
        nextArrow: <CustomArrow direction="next"/>,
        beforeChange: handleBeforeChange,
        customPaging: (i: number) => (
            <div className="custom-dot">
                <span>{slides[i].dotText}</span>
            </div>
        ),
        appendDots: (dots: any) => (
            <>
                <div className="mobile-text no-desktop">
                    <span className="title">Mercedes-Benz Trucks</span><br/>
                    <span className="subtitle">Operating Fluids</span>
                </div>
                <ul className="slick-dots">{dots}</ul>
            </>
        ),
    }), [autoplaySpeed, slides, handleBeforeChange]);

    return (
        <div className="carousel-container">
            <ReactSlickSlider className="custom-slick-sliders" {...settings}>
                {slides.map((slide) => (
                    <div key={slide.id} className="slide-container">
                        <div className='image-wrapper'>
                            <img src={slide.image} alt={slide.title} className="slide-image"/>
                        </div>
                        <div className="custom-slider-text wb-type-heading-l desktop-title-text no-mobile">
                            <span className="title">{slide.title}</span><br/>
                            <span className="subtitle">{slide.subtitle}</span>
                        </div>
                        <div className="hotspot-links">
                            {slide.hotspots.map((hotspot: any, index: number) => (
                                <HotspotLink key={index} hotspot={hotspot}/>
                            ))}
                        </div>
                    </div>
                ))}
            </ReactSlickSlider>
            <ProgressBar progress={progress}/>
        </div>
    );
};

export default HomeCarousel;